import {
  createContext,
  useContext,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";

export interface StepStateValue<T> {
  state: T;
  setState: Dispatch<SetStateAction<T>>;
}

// Both halves of the flow (pilot, collection) share state across their steps
// through a provider at the group route; this builds one for a state shape.
export function createStepStateContext<T extends object>(name: string) {
  const Context = createContext<StepStateValue<T> | null>(null);

  function Provider({
    children,
    initialState,
  }: {
    children: ReactNode;
    initialState?: T;
  }) {
    const [state, setState] = useState<T>(initialState ?? ({} as T));
    const value = useMemo(() => ({ state, setState }), [state]);
    return <Context.Provider value={value}>{children}</Context.Provider>;
  }
  Provider.displayName = `${name}StateProvider`;

  function useStepState(): StepStateValue<T> {
    const value = useContext(Context);
    if (!value) {
      throw new Error(
        `use${name}State must be used within a ${name}StateProvider`,
      );
    }
    return value;
  }

  return { Provider, useStepState };
}
